/**
 * Best-effort, dependency-free User-Agent → friendly device label, e.g. "Chrome · macOS".
 * Deliberately coarse: we only need enough for a person to recognise their own session,
 * and the parsing runs on the web side so the API can stay reusable and return the raw UA.
 */

export interface ParsedUserAgent {
  browser: string | null;
  os: string | null;
}

const BROWSERS: ReadonlyArray<{ label: string; pattern: RegExp }> = [
  { label: 'Edge', pattern: /Edg(?:e|A|iOS)?\//i },
  { label: 'Opera', pattern: /OPR\/|Opera/i },
  { label: 'Samsung Internet', pattern: /SamsungBrowser/i },
  { label: 'Firefox', pattern: /Firefox\/|FxiOS/i },
  { label: 'Chrome', pattern: /Chrome\/|CriOS/i },
  { label: 'Safari', pattern: /Safari\// },
];

const OPERATING_SYSTEMS: ReadonlyArray<{ label: string; pattern: RegExp }> = [
  { label: 'Windows', pattern: /Windows NT/i },
  { label: 'Android', pattern: /Android/i },
  { label: 'iOS', pattern: /iPhone|iPad|iPod/i },
  { label: 'macOS', pattern: /Mac OS X|Macintosh/i },
  { label: 'ChromeOS', pattern: /CrOS/i },
  { label: 'Linux', pattern: /Linux/i },
];

export function parseUserAgent(ua: string | null | undefined): ParsedUserAgent {
  if (!ua) return { browser: null, os: null };
  const browser = BROWSERS.find((b) => b.pattern.test(ua))?.label ?? null;
  const os = OPERATING_SYSTEMS.find((o) => o.pattern.test(ua))?.label ?? null;
  return { browser, os };
}

/** A single human-readable label, or null when nothing recognisable could be parsed. */
export function deviceLabel(ua: string | null | undefined): string | null {
  const { browser, os } = parseUserAgent(ua);
  if (browser && os) return `${browser} · ${os}`;
  return browser ?? os ?? null;
}
